import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
} from '@mui/material';
import { ViewKanban, ViewList, Search } from '@mui/icons-material';
import { salesEvaluationsAPI } from '../services/api';
import { SalesEvaluation, EvaluationStatus } from '../types/sales';
import EvaluationsBoard from '../components/EvaluationsBoard';

const getStatusLabel = (status: EvaluationStatus): string => {
  const labels: Record<EvaluationStatus, string> = {
    DRAFT: 'Borrador',
    LEAD: 'Lead',
    PROSPECT: 'Prospecto',
    OPPORTUNITY: 'Oportunidad',
    NEGOTIATING: 'Negociación',
    CLOSED: 'Cerrado',
    LOST: 'Perdido',
  };
  return labels[status] || status;
};

const SalesPipeline: React.FC = () => {
  const [evaluations, setEvaluations] = useState<SalesEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  const [search, setSearch] = useState('');

  const loadEvaluations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await salesEvaluationsAPI.getAll();
      setEvaluations(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Error al cargar evaluaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvaluations();
  }, [loadEvaluations]);

  const handleStatusChange = async (evaluationId: string, newStatus: EvaluationStatus) => {
    try {
      await salesEvaluationsAPI.updateStatus(evaluationId, newStatus);
      loadEvaluations();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDelete = async (evaluationId: string) => {
    if (window.confirm('¿Eliminar esta evaluación?')) {
      try {
        await salesEvaluationsAPI.delete(evaluationId);
        loadEvaluations();
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Error al eliminar');
      }
    }
  };

  const handleGenerateProposal = async (evaluation: SalesEvaluation) => {
    try {
      const blob = await salesEvaluationsAPI.generateProposal(evaluation.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Propuesta_${evaluation.evaluation_name}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al generar propuesta');
    }
  };

  // Filter evaluations by search
  const filtered = search
    ? evaluations.filter(
        (e) =>
          e.evaluation_name.toLowerCase().includes(search.toLowerCase()) ||
          (e.prospect_name || '').toLowerCase().includes(search.toLowerCase()) ||
          (e.client_id || '').toLowerCase().includes(search.toLowerCase()) ||
          (e.created_by_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : evaluations;

  // Summary counts
  const statusCounts = evaluations.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">Sales Pipeline</Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="board"><ViewKanban /></ToggleButton>
            <ToggleButton value="list"><ViewList /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <TextField
          size="small"
          placeholder="Buscar por nombre, cliente, vendedor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search /></InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="textSecondary">
          Total: <strong>{evaluations.length}</strong>
        </Typography>
        {Object.entries(statusCounts).map(([status, count]) => (
          <Typography key={status} variant="body2" color="textSecondary">
            | {getStatusLabel(status as EvaluationStatus)}: <strong>{count}</strong>
          </Typography>
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 && !search ? (
        <Alert severity="info">No hay evaluaciones de venta en el sistema.</Alert>
      ) : viewMode === 'board' ? (
        <EvaluationsBoard
          evaluations={filtered}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onGenerateProposal={handleGenerateProposal}
        />
      ) : (
        /* List View */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filtered.map((evaluation) => (
            <Box
              key={evaluation.id}
              sx={{
                p: 2,
                bgcolor: 'white',
                borderRadius: 1,
                boxShadow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {evaluation.evaluation_name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {evaluation.client_id
                    ? `Cliente: ${evaluation.client_id}`
                    : `Prospecto: ${evaluation.prospect_name}`}
                  {evaluation.created_by_name && ` — Vendedor: ${evaluation.created_by_name}`}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 100 }}>
                ${parseFloat(evaluation.total_monthly_payment).toFixed(2)}/mes
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ minWidth: 60 }}>
                {evaluation.contract_months} meses
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 500 }}>
                {getStatusLabel(evaluation.status)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SalesPipeline;
